/** URL query key for deep-linking to one Excel transaction on the entity Transactions tab. */
export const SELECT_TRANSACTION_URL_PARAM = 'selectTransaction';

export function parseSelectTransactionFromSearch(
  search: string
): string | undefined {
  const id = new URLSearchParams(search)
    .get(SELECT_TRANSACTION_URL_PARAM)
    ?.trim();
  return id || undefined;
}

/** Removes `selectTransaction` from a query string (returns string without leading `?`). */
export function removeSelectTransactionFromSearch(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(SELECT_TRANSACTION_URL_PARAM);
  return params.toString();
}

/** Converts ScopeTable filter objects to the API request body shape. */
export function convertScopeTableFiltersToApiBody(
  filters?: Record<string, unknown>
): Record<string, unknown> | undefined {
  if (!filters) return undefined;
  const result: Record<string, unknown> = {};
  Object.keys(filters).forEach((key) => {
    const filterObject = filters[key] as { value?: unknown[] } | undefined;
    if (filterObject && Array.isArray(filterObject.value)) {
      result[key] = filterObject.value;
    }
  });
  return Object.keys(result).length ? result : undefined;
}

/** ScopeTable-shaped filter for queried Excel transactions by row uuid. */
export function appendSelectTransactionUuidFilter(
  filters: Record<string, unknown> | undefined,
  transactionUuid: string
): Record<string, unknown> {
  const next = { ...(filters ?? {}) };
  next.uuid = { value: [transactionUuid] };
  return next;
}

export type TransactionsTableRefreshParams = {
  page: number;
  pageSize: number;
  filters?: Record<string, unknown>;
  /**
   * All excelMetadataIds for the refresh scope: either the active
   * Captured / Reviewed / Posted tab, or the current file-panel selection.
   */
  tabFileImportIds?: string[];
};

/**
 * File IDs to send on queried-transactions requests.
 *
 * Providers with Captured / Reviewed / Posted tabs always send the active-tab
 * (or selected-within-tab) ids — including `[]` when that tab has no files.
 *
 * Excel has no state tabs. An empty file-panel selection means "all files",
 * so the filter is omitted; clicking a file sends that file's metadata id
 * and import id (Excel rows may be keyed by either).
 */
export function resolveTabFileImportIdsForRequest(options: {
  showFileStateTabs: boolean;
  effectiveSelectedImportIds: string[];
  files?: ReadonlyArray<{
    excelMetadataId?: string | null;
    importId?: string | null;
  }>;
}): string[] | undefined {
  if (options.showFileStateTabs) {
    return options.effectiveSelectedImportIds;
  }
  if (options.effectiveSelectedImportIds.length === 0) {
    return undefined;
  }

  const ids = new Set<string>();
  for (const id of options.effectiveSelectedImportIds) {
    const normalized = String(id ?? '').trim();
    if (normalized) ids.add(normalized);
    const file = options.files?.find(
      (item) =>
        String(item.excelMetadataId ?? '').trim() === normalized ||
        String(item.importId ?? '').trim() === normalized
    );
    if (!file) continue;
    const meta = String(file.excelMetadataId ?? '').trim();
    const imp = String(file.importId ?? '').trim();
    if (meta) ids.add(meta);
    if (imp) ids.add(imp);
  }
  return [...ids];
}

/**
 * Builds refresh filters: table sort/column filters only, plus optional file IDs
 * (active tab and/or current file-panel selection).
 * Strips any excelMetadataId / importId on the incoming table filters so a stale
 * single-file subset from save time cannot leak through.
 */
export function buildTransactionsRefreshFilters(options: {
  tableFilters?: Record<string, unknown>;
  tabFileImportIds?: string[];
}): Record<string, unknown> | undefined {
  const next: Record<string, unknown> = {};
  const tableFilters = options.tableFilters ?? {};
  Object.keys(tableFilters).forEach((key) => {
    if (
      key === 'excelMetadataId' ||
      key === 'importId' ||
      key === 'reviewStatus' ||
      key === 'reversalType'
    ) {
      return;
    }
    next[key] = tableFilters[key];
  });

  // Apply tab scope whenever defined — including `[]` for an empty tab (no matching files).
  if (options.tabFileImportIds !== undefined) {
    next.excelMetadataId = { value: options.tabFileImportIds };
  }

  return Object.keys(next).length > 0 ? next : undefined;
}

/**
 * @deprecated Prefer buildTransactionsRefreshFilters with tabFileImportIds.
 */
export function stripEntityFileImportFilters(
  filters?: Record<string, unknown>
): Record<string, unknown> | undefined {
  return buildTransactionsRefreshFilters({
    tableFilters: filters,
  });
}
